import { useTheme } from '@mui/material/styles';
import { RefCallback, useCallback, useMemo, useRef, useState } from 'react';

import { trackPointerHover } from '../utils/trackPointerHover';

export type LegendInteraction = {
  hoverKey: string | null;
  hiddenKeys: Set<string>;
  onLegendMouseEnter: (payload: any) => void;
  onLegendMouseLeave: () => void;
  onLegendClick: (payload: any) => void;
  legendFormatter: (value: any, entry: any) => JSX.Element;
  isSeriesHidden: (key: string) => boolean;
  isSeriesDimmed: (key: string) => boolean;
  legendContainerProps: {
    ref: RefCallback<HTMLElement>;
  };
  legendItemProps: (key: string) => { 'data-legend-key': string };
};

export const getLegendPayloadKey = (payload: any) =>
  String(
    payload?.dataKey ??
      payload?.payload?.dataKey ??
      payload?.payload?.value ??
      payload?.value ??
      ''
  );

export function useLegendInteraction(): LegendInteraction {
  const theme = useTheme();
  const fontFamily = theme.typography.fontFamily;
  const textColor = theme.palette.text.primary;
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const legendElementRef = useRef<HTMLElement | null>(null);
  const stopTrackingRef = useRef<(() => void) | null>(null);

  const onLegendMouseEnter = useCallback((o: any) => {
    const key = getLegendPayloadKey(o);
    setHoverKey(key);
  }, []);

  const onLegendMouseLeave = useCallback(() => setHoverKey(null), []);

  const setLegendElement = useCallback((element: HTMLElement | null) => {
    stopTrackingRef.current?.();
    stopTrackingRef.current = null;
    legendElementRef.current = element;
    if (!element) {
      queueMicrotask(() => {
        if (!legendElementRef.current) setHoverKey(null);
      });
      return;
    }
    stopTrackingRef.current = trackPointerHover(element, {
      onMove: (event) => {
        const item = (event.target as Element).closest?.('[data-legend-key]');
        const key = item?.getAttribute('data-legend-key') ?? null;
        setHoverKey((prev) => (prev === key ? prev : key));
      },
      onLeave: () => setHoverKey(null),
    });
  }, []);

  const legendContainerProps = useMemo(
    () => ({ ref: setLegendElement }),
    [setLegendElement]
  );

  const legendItemProps = useCallback(
    (key: string) => ({ 'data-legend-key': key }),
    []
  );

  const onLegendClick = useCallback((o: any) => {
    const key = getLegendPayloadKey(o);
    if (!key) return;

    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const legendFormatter = useCallback(
    (value: any, entry: any) => {
      const key = getLegendPayloadKey(entry) || String(value);
      const isHidden = hiddenKeys.has(key);
      return (
        <span
          style={{
            fontFamily,
            color: textColor,
            fontSize: 13,
            opacity: isHidden ? 0.3 : 1,
            cursor: 'pointer',
          }}
        >
          {value}
        </span>
      );
    },
    [hiddenKeys, fontFamily, textColor]
  );

  const isSeriesHidden = useCallback(
    (key: string) => hiddenKeys.has(key),
    [hiddenKeys]
  );
  const isSeriesDimmed = useCallback(
    (key: string) => hoverKey != null && hoverKey !== key,
    [hoverKey]
  );

  return {
    hoverKey,
    hiddenKeys,
    onLegendMouseEnter,
    onLegendMouseLeave,
    onLegendClick,
    legendFormatter,
    isSeriesHidden,
    isSeriesDimmed,
    legendContainerProps,
    legendItemProps,
  };
}
